import { InverseHeader } from '@jouzen/ecom-components/layout';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import type { ReactNode } from 'react';

import type { ArtDirectionImageProps } from '@/app/components/Image/ArtDirectionImage';
import ArtDirectionImage from '@/app/components/Image/ArtDirectionImage';
import AccuracyCard, {
  type AccuracyCardProps,
} from '@/app/components/LandingPage/LandingPageAccuracy/AccuracyCard';
import MobileAccuracyCard from '@/app/components/LandingPage/LandingPageAccuracy/MobileAccuracyCard';
import SectionHeading from '@/app/components/SectionHeading';

import TabletAccuracyCard from './TabletAccuracyCard';

interface BillboardStatsModuleModProps {
  readonly accuracyCards: AccuracyCardProps[];
  readonly content?: ReactNode;
  readonly image: ArtDirectionImageProps;
}

const BillboardStatsModuleMod = ({
  accuracyCards,
  content,
  image,
}: BillboardStatsModuleModProps) => {
  return (
    <LayoutGrid Element="section" data-cy="billboard-stats-module-mod">
      <GridItem
        colEnd={{
          sm: 'main',
          lg: 'full',
        }}
        colStart={{
          sm: 'main',
          lg: 'full',
        }}
        rowStart={{ sm: 1, lg: 1 }}
      >
        <InverseHeader
          className="relative aspect-5/4 overflow-hidden rounded-xl lg:aspect-auto lg:h-[900px] lg:rounded-none"
          enter
        >
          <ArtDirectionImage className="object-cover" fill {...image} />
        </InverseHeader>
      </GridItem>
      <GridItem
        className="flex flex-col justify-between pt-10 pb-4 lg:py-20"
        colEnd={{
          sm: 'main',
          lg: 9,
        }}
        colStart={{
          sm: 'main',
          lg: 'main',
        }}
        rowStart={{ sm: 2, lg: 1 }}
      >
        <SectionHeading
          animated
          content="science_research_2025_sensors_title"
          multiline
          typographyProps={{
            className:
              'mb-6 mt-2 leading-tight tracking-tighter md:justify-start lg:text-sandstone-200',
            color: 'unset',
            Element: 'h3',
            variant: 'h2',
          }}
          wrapperAnimationProps={{
            initial: { filter: 'blur(5px)', opacity: 0 },
            transition: { delay: 0.2, duration: 0.75, type: 'tween' },
          }}
        />
        {content}
      </GridItem>
      <GridItem
        colEnd={{ sm: 'main' }}
        colStart={{ sm: 'main', lg: 18, xl: 20 }}
        rowEnd={{ sm: 4, lg: 3 }}
        rowStart={{ sm: 3, lg: 1 }}
      >
        <div className="flex flex-col md:hidden">
          {accuracyCards.map((card) => (
            <MobileAccuracyCard key={card.heading} {...card} />
          ))}
        </div>
        <div className="hidden grid-cols-2 gap-4 md:grid lg:hidden">
          {accuracyCards.map((card) => (
            <TabletAccuracyCard key={card.heading} {...card} />
          ))}
        </div>
        <div className="hidden h-full flex-col justify-end gap-y-6 py-20 text-sandstone-200 lg:flex">
          {accuracyCards.map((card) => (
            <AccuracyCard key={card.heading} {...card} />
          ))}
        </div>
      </GridItem>
    </LayoutGrid>
  );
};

export default BillboardStatsModuleMod;
